import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Non-home pages always show solid bg; home page transparent until scroll
  const effectiveScrolled = isHome ? scrolled : true;
  const textColor = isHome && !effectiveScrolled ? "text-white" : "text-foreground-900";
  const navBg = effectiveScrolled
    ? "bg-background-50/95 backdrop-blur-md border-background-200/60"
    : "bg-transparent border-transparent";

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${navBg} border-b`}>
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo + Home */}
          <div className="flex items-center gap-6">
            <Link to="/" className={`font-heading text-xl md:text-2xl font-semibold tracking-tight whitespace-nowrap ${textColor} transition-colors duration-300`}>
              Hoobuy Life
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/" className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}>
                <i className="ri-home-4-line mr-1.5"></i>Início
              </Link>

              {/* Categories Dropdown */}
              <div ref={dropdownRef} className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}
                >
                  <i className="ri-grid-line mr-1"></i>Categorias
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-72 bg-background-50 rounded-xl border border-background-200/70 py-2 max-h-80 overflow-y-auto shadow-lg">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-800 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                          <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        {cat.namePt}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/artigo/guia-de-compras-hoobuy" className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}>
                <i className="ri-shopping-bag-3-line mr-1.5"></i>Guia de Compras
              </Link>
              <Link to="/artigo/guia-completo-hoobuy-spreadsheet" className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}>
                Guia
              </Link>
              <a href="/#faq" className={`text-sm font-semibold whitespace-nowrap ${textColor} hover:opacity-70 transition-opacity cursor-pointer`}>
                FAQ
              </a>
            </div>
          </div>

          {/* Right CTA */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer transition-all ${
                isHome && !scrolled
                  ? "bg-white/15 backdrop-blur-sm text-white border border-white/30 hover:bg-white/25"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              hoobuy sheet
              <i className="ri-arrow-right-up-line"></i>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-full whitespace-nowrap cursor-pointer transition-all ${
                isHome && !scrolled
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "bg-foreground-900 text-background-50 hover:bg-foreground-800"
              }`}
            >
              buy it now
              <i className="ri-arrow-right-line"></i>
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center ${textColor} cursor-pointer`}
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-5 flex flex-col gap-1">
            {/* Home */}
            <Link
              to="/"
              className="flex items-center gap-3 py-3 px-2 text-sm font-semibold text-foreground-900 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                <i className="ri-home-4-line"></i>
              </div>
              Início
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/artigo/guia-de-compras-hoobuy"
              className="flex items-center gap-3 py-3 px-2 text-sm font-semibold text-foreground-900 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-shopping-bag-3-line"></i>
              </div>
              Guia de Compras
            </Link>

            {/* Divider */}
            <div className="my-3 border-t border-background-200/60"></div>

            {/* Mobile Categories Grid */}
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide px-2 py-1">Categorias</p>
            <div className="grid grid-cols-2 gap-1.5">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 py-2.5 px-2 text-sm text-foreground-700 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-7 h-7 rounded-md overflow-hidden flex-shrink-0 bg-background-200">
                    <img src={cat.image} alt={cat.namePt} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  {cat.namePt}
                </a>
              ))}
            </div>

            {/* Divider */}
            <div className="my-3 border-t border-background-200/60"></div>

            {/* Other Links */}
            <Link
              to="/artigo/guia-completo-hoobuy-spreadsheet"
              className="flex items-center gap-3 py-3 px-2 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600">
                <i className="ri-article-line"></i>
              </div>
              Guia Completo
            </Link>
            <a
              href="/#faq"
              className="flex items-center gap-3 py-3 px-2 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 text-foreground-600">
                <i className="ri-question-line"></i>
              </div>
              FAQ
            </a>

            {/* Mobile CTAs */}
            <div className="flex flex-col gap-2 mt-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 py-3 px-5 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all cursor-pointer"
              >
                hoobuy sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 py-3 px-5 bg-foreground-900 text-background-50 text-sm font-semibold rounded-full hover:bg-foreground-800 transition-all cursor-pointer"
              >
                buy it now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}